import { lightTheme } from '@/customTheme'
import { styled } from 'baseui'
import { CSSProperties, useEffect, useState } from 'react'
import { ActionItem } from './ActionItem'
import ButtonCustom from '@/components/ButtonCustom'
import { KIND, SHAPE } from 'baseui/button'
import { SizeButton } from '@/constants/sizeButton'
import Icons from '../Icons'
import { FaceSwapDataObject } from './faceSwapStaticData'
import { useTranslation } from 'react-i18next'
import { Input } from 'baseui/input'
import AutoScroll from '@/components/AutoScroll'
import FaceSwapperGrid from './FaceSwapperGrid'
import GradientSpinner from '@/components/GradientSpinner'
import { customAmplitude } from '@/utils/customAmplitude'
import { InspectorPanelType, useInspector } from '../Toolbox/InspectorDataProvider'
import useAppContext from '@/hooks/useAppContext'
import { useEditorContext } from '@/scenes/engine'
import StarDefaultIcon from '@/components/icons/StarDefaultIcon'

/** UL for the action items */
const ActionList = styled('ul', {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
})

/** LI for each action item */
const ActionItemRow = styled('li', (props: {disabled: boolean, $isLoading: boolean}) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '6px 8px 6px 8px',
  borderRadius: '8px',
  cursor: props.disabled ? 'none' : 'pointer',
  pointerEvents: props.disabled ? 'none' : 'auto',
  background: props.$isLoading ? 'linear-gradient(111deg, rgba(255, 24, 102, 0.10) 6.04%, rgba(253, 1, 103, 0.10) 22.35%, rgba(240, 1, 136, 0.10) 43.65%, rgba(221, 1, 160, 0.10) 53.87%, rgba(210, 3, 180, 0.10) 67.14%, rgba(193, 14, 196, 0.10) 84.18%, rgba(153, 1, 156, 0.10) 105.87%)' : props.disabled ? lightTheme.colors.grayScale20 : lightTheme.colors.grayScale50,
  transition: 'background-color 0.15s ease',
  ':hover': {
    background: lightTheme.colors.grayScale100,
  },
}))
interface ActionMenuProps {
  actions: ActionItem[]
  onSubmit?: (inputValue: string, hasFace: boolean) => void
  loading?: boolean
  onActionSelect?: (action: ActionItem, hasFace: boolean, callback?: () => void) => void
  hasFaces: boolean
  hasPremium: boolean
  isLoggedIn: boolean
  onShowMore?: () => void
  onClose?: () => void
  genImageLoading: boolean
  genImageError: string
  onClearError?: () => void
  onSignIn?: () => void
  isLoadingFromText: boolean
  hasInput?: boolean
  hasQuickActions?: boolean
  title?: string
  hasHeader?: boolean
  hasShowMoreBtn?: boolean
  wrapMenuStyle?: CSSProperties
  hasScroll?: boolean
}

/** Container styled to mimic a pop-up card-like layout */
const Container = styled('div', ({ $theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '320px',
  height: '440px',
  borderRadius: '16px',
  backgroundColor: $theme.colors.backgroundPrimary,
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  // fontFamily: $theme.typography.FontFamily,
}))

const Content = styled('div', ({ $theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '330px',
  overflow: 'scroll',
}))

const HeaderRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  margin: '16px',
})

/** Title at the top of the card */
const Title = styled('h2', ({ $theme }) => ({
  ...lightTheme.typography.Small14Semibold,
  color: lightTheme.colors.blackGray,
}))

/** "Suggested" label */
const SuggestedLabel = styled('span', ({ $theme }) => ({
  ...lightTheme.typography.Small11medium,
  color: lightTheme.colors.blackGray,
  display: 'inline-block',
}))

const Chevron = styled('i', ({ $theme }) => ({
  ...lightTheme.typography.Small11medium,
  marginLeft: '2px',
}))

const SuggestedSection = styled('div', ({ $theme }) => ({
  ...lightTheme.typography.Small11medium,
  marginBottom: '8px',
  display: 'flex',
  justifyContent: 'space-between',
}))

/** Optional icon to show on each item */
const ActionItemIcon = styled('span', (props: any) => ({
  width: '22px',
  height: '22px',
  display: 'inline-flex',
  justifyContent: 'center',
  alignItems: 'center',
  fontSize: '1.2rem',
  marginRight: '8px',
  opacity: props.disabled ? 0.5 : 1,
}))

/** Text of the item */
const ActionItemTitle = styled('span', (props: any) => ({
  ...lightTheme.typography.Small12medium,
  color: props.disabled ? lightTheme.colors.grayScale200 : lightTheme.colors.blackGray,
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}))
const ActionItemError = styled('span', ({ $theme }) => ({
  ...lightTheme.typography.Small12medium,
  lineHeight: '20px',
}))

/** SkeletonRow used when loading */
const SkeletonRow = styled('li', ({ $theme }) => ({
  backgroundColor: lightTheme.colors.grayScale100,
  height: '36px',
  borderRadius: '8px',
  animation: 'pulse 1.5s ease-in-out infinite',
}))

// Send button style
const SendButton = styled('button', (props: { $disabled?: boolean; $isActive?: boolean }) => ({
  position: 'absolute',
  width: '28px',
  height: '28px',
  padding: 0,
  border: 'none',
  outline: 'none',
  background: props.$disabled ? lightTheme.colors.grayScale50 : lightTheme.colors.primary,
  borderRadius: '50%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  pointerEvents: props.$disabled ? 'none' : 'auto',
  cursor: 'pointer',
  ':hover': {
    background: 'rgba(229, 5, 86, 1)',
  },
  ':active': {
    background: 'rgba(204, 4, 77, 1)',
  },
  filter: props.$disabled ? 'none' : 'drop-shadow(0px 0px 12px rgba(255, 5, 96, 0.25))',
  top: '8px', 
  right: '24px'
}))

const GradientText = styled('span', {
  background:
    'linear-gradient(125deg, #FF1866 2.53%, #FD0167 18.16%, #F00188 38.58%, #DD01A0 48.37%, #D203B4 61.09%, #C10EC4 77.43%, #99019C 98.21%)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  fontWeight: 600,
})
const ActionMenu: React.FC<ActionMenuProps> = ({
  actions,
  onActionSelect,
  hasFaces,
  isLoggedIn,
  loading,
  onSubmit,
  hasPremium,
  onShowMore,
  onClose,
  genImageLoading,
  genImageError,
  onClearError,
  onSignIn,
  isLoadingFromText,
  hasInput,
  hasQuickActions,
  title = '',
  hasHeader,
  hasShowMoreBtn,
  wrapMenuStyle,
  hasScroll = true
}) => {
  const [inputValue, setInputValue] = useState('')
  const [suggestionOptionLoading, setSuggestionOptionLoading] = useState(null)
  const isInputDisabled = genImageLoading || genImageError.length > 0
  let isSubmitDisabled = false;
  if (isInputDisabled) {
    isSubmitDisabled = true
  } else if (loading && inputValue.trim().length) {
    isSubmitDisabled = false
  } else if (!inputValue.trim().length) {
    isSubmitDisabled = true
  }
  const { t } = useTranslation()
  const { addInspector } = useInspector()
  const { setToolType } = useAppContext()
  const { editor, activeObject } = useEditorContext()
  const defaultIcon = <StarDefaultIcon width={22} height={22} />

  useEffect(() => {
    if(!genImageLoading) {
      setSuggestionOptionLoading(null)
    }
  }, [genImageLoading])

  const handleItemClick = (action: ActionItem) => {
    if (onActionSelect) {
      onActionSelect(action, hasFaces, () => setSuggestionOptionLoading(action.title))
    }
  }

  const handleSubmit = () => {
    if (onSubmit) {
      onSubmit(inputValue, hasFaces)
    }
  }

  const onClickRemoveBg = () => {
    editor.handlers.removeBackgroundHandler.removeBackgroundWithUIState(activeObject, onClose)
    customAmplitude('AI Edit Local Tool', {
      Tool: 'remove_bg'
    })
  }

  // if (!isLoggedIn) {
  // return (
  //     <Container style={{ position: 'relative' }}>
  //         <LoginOverlaySimple onSignIn={onSignIn} />
  //     </Container>
  // );
  // }

  const onClickChangeStyle = () => {
    addInspector(InspectorPanelType.MagicTemplates)
    setToolType('MagicTemplates')
    customAmplitude('AI Edit Local Tool', {
      Tool: 'change_style'
    })
  }

  return (
    <Container style={{ height: hasInput ? '440px' : '480px', ...wrapMenuStyle}}>
      <style>{`
            @keyframes pulse {
                50% {
                    opacity: 0.5;
                }
            }
        `}</style>
      {hasHeader ? (
        <HeaderRow>
          <Title>{t(title)}</Title>
          <ButtonCustom
            shape={SHAPE.square}
            kind={KIND.minimal}
            type={SizeButton.SMALL}
            onClick={onClose}
            style={{ borderRadius: '360px' }}
          >
            <Icons.Close size={24} fill={lightTheme.colors.blackGray} />
          </ButtonCustom>
        </HeaderRow>
      ) : null}

      {
        hasInput ? <div style={{ paddingLeft: '16px', paddingRight: '16px', paddingTop: '0px', position: 'relative' }}>
        <Input
          value={inputValue}
          onChange={e => setInputValue((e.target as HTMLInputElement).value)}
          placeholder="What would you like to change?"
          onKeyDown={e => {
            if (e.key === 'Enter' && !isSubmitDisabled) {
              handleSubmit()
            }
          }}
          overrides={{
            Root: {
              style: ({ $theme, $isFocused }) => ({
                marginBottom: '16px',
                borderWidth: '0px !important',
                transition: 'border-color 0.2s ease',
                backgroundColor: lightTheme.colors.white,
                height: '44px',
              }),
            },
            InputContainer: {
              style: {
                outline: 'none',
                border: 'none',
              },
            },
            Input: {
              style: ({ $theme }) => ({
                fontSize: '14px',
                padding: '12px 44px 12px 12px',
                width: '100%',
                border: `1px solid ${lightTheme.colors.grayScale100}`,
                borderRadius: '12px !important',
                color: inputValue ? lightTheme.colors.blackGray : lightTheme.colors.grayScale400,
                ':hover': {
                  borderColor: lightTheme.colors.grayScale300,
                },
                ':focus': {
                  borderColor: lightTheme.colors.grayScale300,
                },
              }),
            },
          }}
        />
        <SendButton
          $disabled={isSubmitDisabled}
          onClick={(e) => {
              e.stopPropagation();
              if (!isSubmitDisabled) {
                  handleSubmit();
              }
          }}
      >
          <Icons.FullArrowRightIcon fill={isSubmitDisabled ? 'rgba(204, 204, 204, 1)' : '#FFF'} />
          {(!hasPremium) ? <div style={{ position: 'absolute', top: '-4px', right: '-5px' }}><Icons.PremiumIcon disabled={isSubmitDisabled} /></div> : null}
      </SendButton>
      </div> : null
      }
      <AutoScroll
        style={
          hasScroll
            ? {
                marginTop: 0,
                paddingRight: '4px',
                paddingLeft: '16px',
                paddingTop: '0px',
              }
            : { padding: 0, overflow: 'hidden', margin: 0 }
        }
      >
        {genImageLoading && isLoadingFromText && (
          <>
            <SuggestedLabel style={{marginBottom: '8px'}}>{t('Generating')}</SuggestedLabel>
            <ActionList>
              <ActionItemRow $isLoading disabled>
                <ActionItemIcon>
                  <GradientSpinner />
                </ActionItemIcon>
                <ActionItemTitle>
                  <GradientText>{t('Just a sec...')}</GradientText>
                </ActionItemTitle>
              </ActionItemRow>
            </ActionList>
          </>
        )}

        {genImageError && !genImageLoading && (
          <>
            <div
              onClick={onClearError}
              style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                marginBottom: '8px',
                height: '16px'
              }}
            >
              <ActionItemIcon style={{ marginRight: '4px', width: '6px', height: '10px' }}>
                <Icons.ChevronLeft/>
              </ActionItemIcon>
              <span style={{ ...lightTheme.typography.Small11medium }}>{t('Back to Suggested')}</span>
            </div>
            <ActionList>
              <ActionItemRow>
                <ActionItemIcon>
                  <Icons.Info />
                </ActionItemIcon>
                <ActionItemError>
                  {t('Oops! We can’t edit this photo. Try using another one.')}
                </ActionItemError>
              </ActionItemRow>
            </ActionList>
          </>
        )}

        {!genImageError && (
          <div style={{ display: isLoadingFromText ? 'none' : 'flex',justifyContent: 'start', flexDirection: 'column', }}>
             {hasQuickActions && (
              <>
                <SuggestedLabel>{t('Quick actions')}</SuggestedLabel>
                <ActionList style={{margin: '8px 0 16px'}}>
                  <ActionItemRow onClick={() => onClickRemoveBg()} disabled={genImageLoading}>
                    <ActionItemIcon disabled={genImageLoading}>
                      <Icons.RemoveBgIcn size={22} />
                    </ActionItemIcon>
                    <ActionItemTitle disabled={genImageLoading}>{t('Remove background')}</ActionItemTitle>
                  </ActionItemRow>
                  <ActionItemRow onClick={() => onClickChangeStyle()} disabled={genImageLoading}>
                    <ActionItemIcon disabled={genImageLoading}>
                      <Icons.MagicStyleIcn size={22} />
                    </ActionItemIcon>
                    <ActionItemTitle disabled={genImageLoading}>{t('Change style')}</ActionItemTitle>
                  </ActionItemRow>
                </ActionList>
              </>
            )}
            {(
              <>
                {hasQuickActions ? <SuggestedLabel style={hasHeader ? {} : {...lightTheme.typography.Small14Semibold}}>{t('Suggested')}</SuggestedLabel> : null}
                <ActionList style={{margin: `${hasQuickActions ? 8 : 0}px 0 16px`}}>
                  {loading ? (
                    <>
                      <SkeletonRow />
                      <SkeletonRow />
                      <SkeletonRow />
                      <SkeletonRow />
                      <SkeletonRow />
                    </>
                  ) : (
                    <>
                      {actions.map((action, index) => (
                        <ActionItemRow key={index} onClick={() => handleItemClick(action)} disabled={genImageLoading && action.title !== suggestionOptionLoading} style={{
                          pointerEvents: genImageLoading ? 'none' : 'auto',
                          borderRadius: !hasHeader ? '360px' : 'auto'
                        }}
                        $isLoading={genImageLoading && action.title === suggestionOptionLoading}
                        >
                          <ActionItemIcon disabled={genImageLoading && action.title !== suggestionOptionLoading}>{suggestionOptionLoading && action.title === suggestionOptionLoading ? <GradientSpinner /> : action.icon || defaultIcon}</ActionItemIcon>
                          <ActionItemTitle disabled={genImageLoading && action.title !== suggestionOptionLoading}>{suggestionOptionLoading && action.title === suggestionOptionLoading? <GradientText>{action.title}</GradientText> : action.title}</ActionItemTitle>
                          {!hasPremium && (
                            <ActionItemIcon style={{ margin: '0 0 0 auto', width: '16px', height: '16px' }} disabled={genImageLoading && action.title !== suggestionOptionLoading}>
                              <Icons.PremiumIcon />
                            </ActionItemIcon>
                          )}
                        </ActionItemRow>
                      ))}
                      {hasShowMoreBtn && onShowMore && (
                        <ActionItemRow onClick={() => onShowMore()} disabled={genImageLoading}>
                          <ActionItemIcon disabled={genImageLoading}>
                            <Icons.MagicStars size={22} />
                          </ActionItemIcon>
                          <ActionItemTitle disabled={genImageLoading}>{t('New suggestions')}</ActionItemTitle>
                          {!hasPremium && (
                            <ActionItemIcon style={{ margin: '0 0 0 auto', width: '16px', height: '16px' }} disabled={genImageLoading}>
                              <Icons.PremiumIcon />
                            </ActionItemIcon>
                          )}
                        </ActionItemRow>
                      )}
                    </>
                  )}
                </ActionList>
              </>
            )}
          </div>
        )}
      </AutoScroll>
    </Container>
  )
}
export default ActionMenu
